import React, { useEffect, useState } from "react"

import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { isHivedEmail } from "@/constants/hived"
import { ALL_LOCATIONS, ALL_SHERIFF_AREAS } from "@/constants/sheriffOptions"
import { CreateUserData, useCreateUser } from "@/hooks/useCreateUser"
import { baseSubTeamValues, useSubTeams } from "@/hooks/useSubTeams"

import { Constants } from "../../../database.types"

interface CreateUserDialogProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  org: "hived" | "dsp"
  kind: "staff" | "drivers"
}

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  isOpen,
  setIsOpen,
  org,
  kind,
}) => {
  const [formData, setFormData] = useState<{
    firstName: string
    surname: string
    email: string
    role: string
    team: string
    dsp_id?: string
  }>({
    firstName: "",
    surname: "",
    email: "",
    role: "Admin",
    team: "",
    dsp_id: "",
  })
  const [selectedSubTeams, setSelectedSubTeams] = useState<string[]>([])
  const [functionAreas, setFunctionAreas] = useState<string[]>([])
  const [location, setLocation] = useState<string[]>([])

  // Use the hook to fetch sub-teams
  const { subTeamOptions, subTeamOptionsWithIds } = useSubTeams()

  // A HIVED people row is created for Admin/Manager users invited with an
  // @hived.space email; only then are the person fields relevant.
  const isHivedPerson =
    (formData.role === "Admin" || formData.role === "Manager") && isHivedEmail(formData.email)

  // Filter subteam options based on role
  const filteredSubTeamOptions = React.useMemo(() => {
    if (formData.role === "DSP") {
      return subTeamOptionsWithIds
        .filter((option) => option.type === "DSP")
        .map((t) => ({ value: t.value, label: t.label }))
    }
    // Managers and other roles see all options
    return subTeamOptions
  }, [formData.role, subTeamOptions, subTeamOptionsWithIds])

  // Set default role when dialog opens
  useEffect(() => {
    if (!isOpen) return

    setFormData((prev) => ({
      ...prev,
      role: org === "dsp" ? "DSP" : "Admin",
      team: org === "dsp" ? "DSP Manager" : "",
    }))
  }, [isOpen, org])

  // Clear selected subteams when role changes to prevent invalid selections
  useEffect(() => {
    setSelectedSubTeams([])
    setFormData((prev) => ({
      ...prev,
      dsp_id: "",
    }))
  }, [formData.role])

  // Handle sub-team selection with special logic for DSP role
  const handleSubTeamChange = (newSubTeams: string[]) => {
    if (formData.role === "DSP") {
      // For DSP role, only allow single selection
      const singleSelection = newSubTeams.length > 0 ? [newSubTeams[newSubTeams.length - 1]] : []
      setSelectedSubTeams(singleSelection)

      // Auto-set dsp_id when DSP sub-team is selected
      if (singleSelection.length > 0) {
        const selectedDsp = subTeamOptionsWithIds.find((t) => t.value === singleSelection[0])
        if (selectedDsp) {
          setFormData((prev) => ({
            ...prev,
            dsp_id: selectedDsp.id,
          }))
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          dsp_id: "",
        }))
      }
    } else {
      // For other roles, allow multiple selections
      setSelectedSubTeams(newSubTeams)
    }
  }

  // Use the create user mutation
  const createUser = useCreateUser()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Block creation via UI for drivers
    if (kind === "drivers") {
      toast.error("Drivers cannot be created via the UI")
      return
    }

    // Validate form data
    if (!formData.firstName || !formData.surname || !formData.email) {
      toast.error("Please fill in first name, surname and email")
      return
    }
    if (org === "dsp" && (!formData.dsp_id || formData.dsp_id.trim() === "")) {
      toast.error("DSP ID is required for DSP users")
      return
    }

    // Only include base subteams (HIVED, Self Employed) for Admin and Manager roles, not for DSP
    const combinedSubTeams = Array.from(
      new Set([...(selectedSubTeams ?? []), ...(formData.role !== "DSP" ? baseSubTeamValues : [])]),
    )

    const selectedSubteamMeta = combinedSubTeams
      .map((value) => subTeamOptionsWithIds.find((t) => t.value === value))
      .filter((option): option is (typeof subTeamOptionsWithIds)[number] => Boolean(option))

    const dspSelection = selectedSubteamMeta.find((option) => option.type === "DSP")
    const managerSelection = selectedSubteamMeta.find(
      (option) => option.type === "DRIVER_MANAGER_TEAM",
    )

    // Prepare user data for submission
    const userData: CreateUserData = {
      ...formData,
      ...(formData.team && { team: formData.team }),
      sub_teams: combinedSubTeams,
      // For DSP role: set dsp_id to the subteam ID
      ...(formData.role === "DSP" && dspSelection?.id ? { dsp_id: dspSelection.id } : {}),
      // For Manager role: set driver_manager_team_id to the subteam ID
      ...(formData.role === "Manager" && managerSelection?.id
        ? { driver_manager_team_id: managerSelection.id }
        : {}),
      // HIVED-person fields: only meaningful when a people row will be created.
      ...(isHivedPerson
        ? {
            functionAreas,
            location,
            ...(managerSelection?.value ? { driver_manager_team: managerSelection.value } : {}),
          }
        : {}),
    }

    // Submit the user data
    createUser.mutate(userData, {
      onSuccess: () => {
        toast.success("Invitation sent successfully")
        // Reset form and close dialog
        setFormData((prev) => ({
          ...prev,
          firstName: "",
          surname: "",
          email: "",
          role: org === "dsp" ? "DSP" : "Admin",
          team: org === "dsp" ? "DSP Manager" : "",
          dsp_id: "",
        }))
        setSelectedSubTeams([])
        setFunctionAreas([])
        setLocation([])
        setIsOpen(false)
      },
      onError: (error) => {
        toast.error(
          `Failed to create user: ${error instanceof Error ? error.message : "Unknown error"}`,
        )
      },
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{org === "hived" ? "Invite HIVED User" : "Invite DSP User"}</DialogTitle>
          <DialogDescription>
            Send an invitation email and set their access level.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surname">Surname</Label>
                <Input
                  id="surname"
                  placeholder="Doe"
                  value={formData.surname}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">User Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {org === "dsp" ? (
                    <SelectItem value="DSP">DSP</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="team">Team</Label>
              <Select
                value={formData.team}
                disabled={org === "dsp"}
                onValueChange={(value) => {
                  setFormData((prev) => ({ ...prev, team: value }))
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {Constants.public.Enums.app_team.map((team) => (
                    <SelectItem key={team} value={team}>
                      {team}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sub_teams">{formData.role === "DSP" ? "DSP" : "Sub Teams"}</Label>
              <MultiSelect
                options={filteredSubTeamOptions}
                selected={selectedSubTeams}
                onChange={handleSubTeamChange}
                placeholder={formData.role === "DSP" ? "Select DSP" : "Select sub teams"}
              />
              {formData.role === "DSP" && selectedSubTeams.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  DSP ID will be auto-set: {formData.dsp_id}
                </p>
              )}
            </div>

            {isHivedPerson && (
              <>
                <div className="space-y-2">
                  <Label>Function areas</Label>
                  <MultiSelect
                    options={ALL_SHERIFF_AREAS.map((a) => ({ value: a, label: a }))}
                    selected={functionAreas}
                    onChange={setFunctionAreas}
                    placeholder="Select function areas"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Locations</Label>
                  <MultiSelect
                    options={ALL_LOCATIONS.map((loc) => ({ value: loc, label: loc }))}
                    selected={location}
                    onChange={setLocation}
                    placeholder="Select locations"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  This user will also get a person record with a Person ID.
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? "Sending..." : "Send Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
